/**
 * Whether this person has seen one workflow page's tour, and the controls to
 * run it (DOP-625). The KPI dashboard's `useKpiOnboarding` does the same for
 * its tour plus two more flags; this is the tour on its own, for the monitoring
 * and deal-screening pages.
 *
 * The tour opens by itself once, when `autoStart` first holds (the page has
 * something to point at) and the signed-in user has not seen it. "Take the
 * tour" replays it. Seen as soon as it ends, however it ends: a tour that comes
 * back after "Skip" is the nag it exists not to be.
 *
 * Per PERSON (the key carries the user id) and per KIND of workflow, never per
 * workflow: it teaches the page, not one fund's or one deal's contents.
 * `localStorage`, every access guarded, so blocked storage means the tour just
 * runs again next time.
 */

import { useCallback, useEffect, useState } from "react";

import { useOptionalAuth } from "../context/AuthContext";
import { kpiOnboardingKey } from "./useKpiOnboarding";

export const MONITORING_TOUR_SEEN_KEY = "doppler.monitoring.tourSeen";
export const DEAL_SCREENING_TOUR_SEEN_KEY = "doppler.dealScreening.tourSeen";

function readSeen(key: string): boolean {
	try {
		return window.localStorage.getItem(key) === "true";
	} catch {
		return false;
	}
}

export interface WorkflowTourState {
	open: boolean;
	start: () => void;
	end: () => void;
}

export function useWorkflowTour(baseKey: string, autoStart: boolean): WorkflowTourState {
	const auth = useOptionalAuth();
	const userId = auth?.user?.id;
	// No provider (an isolated render) is as resolved as it will ever get;
	// under one, nothing may start until the user is known.
	const ready = auth === undefined || (!auth.loading && Boolean(auth.user));
	const key = kpiOnboardingKey(baseKey, userId);
	const [open, setOpen] = useState(false);

	useEffect(() => {
		if (ready && autoStart && !readSeen(key)) setOpen(true);
	}, [ready, autoStart, key]);

	const start = useCallback(() => setOpen(true), []);
	const end = useCallback(() => {
		setOpen(false);
		try {
			window.localStorage.setItem(key, "true");
		} catch {
			// Unusable storage: the tour just runs again next time.
		}
	}, [key]);

	return { open, start, end };
}
